import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import EmptyList from 'components/EmptyList/EmptyList';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import TextField from 'components/TextField/TextField';
import { DATA_SET_PAGINATION_COUNT, HEAD_IRO_SEND_LOCAL_EMAILS_CELLS } from 'constants/dataSets';
import { SELECTED_ITEMS } from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { MATERIAL_ASSESSMENT_RESPONDENT } from 'constants/materialAssessment';
import useDebouncedCallback from 'hooks/useDebouncedCallback';
import { useGetRecipientListQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import normalizeEmail from 'utils/normalizeEmail';

import type IAnswerTypeOption from
  'pages/MaterialAssessmentPages/MaterialAssessmentSurveyCreation/components/CreateSurvey/interfaces/IAnswerTypeOption';
import type IRespondentRequest from 'store/slices/materialAssessment/interfaces/IRespondentRequest';
import type IRespondentsModal from './interfaces/IRespodentsModal';

import styles from './scss/RespodentsModal.module.scss';

const RESPONDENT_TYPE_MENU_MAX_HEIGHT = 300;

function RespondentsModal({
  open,
  onClose,
  onShareSurvey,
  currentVcId,
  selectedItems,
  isLoading,
  title,
  isInfoTable,
  showHeaderCheckbox,
  handleAddEmail,
  companyId,
  titleSend,
}: IRespondentsModal) {
  const { t } = useTranslation();
  const [respondentList, setRespondentList] = useState<IRespondentRequest[]>([]);
  const [respondentListOffset, setRespondentListOffset] = useState(0);
  const [debouncedRespondentListKeyword, setDebouncedRespondentListKeyword] = useState('');
  const [respondentListKeyword, setRespondentListKeyword] = useState('');
  const [selectedRespondentItems, setSelectedRespondentItems] = useState<IRespondentRequest[]>([]);
  const [selectedRespondentKeysMap, setSelectedRespondentKeysMap] = useState<Record<string, string>>({});
  const [respondentTypesMap, setRespondentTypesMap] = useState<Record<string, string>>({});
  const modalRef = useRef<HTMLDivElement>(null);

  const respondentOptions = Object.keys(MATERIAL_ASSESSMENT_RESPONDENT).map((key) => ({
    label: t(`materialityAssessment.respondentTypes.${key}`, key),
    value: key,
  }));

  const debouncedChangeRespondentListKeyword = useDebouncedCallback((text: string) => {
    setDebouncedRespondentListKeyword(text);
    setRespondentListOffset(0);
  }, 500);

  const handleChangeRespondentListKeyword = (text: string) => {
    setRespondentListKeyword(text);
    debouncedChangeRespondentListKeyword(text);
  };

  const hyphen = t('common.hyphen');

  const emptyListText = (
    <Trans
      i18nKey="common.noResultsWithFilters"
      t={t}
      components={{
        br: <br />,
      }}
    />
  );

  const { data: recipientList, isFetching: isFetchingRecipientList } = useGetRecipientListQuery({
    companyId: companyId || 0,
    offset: respondentListOffset,
    keyword: debouncedRespondentListKeyword,
  }, { skip: !companyId });

  const items = respondentList || [];

  const hasMore = recipientList?.data?.meta?.hasNext || false;

  const isLoadingMore = isFetchingRecipientList;

  const handleResetSelectedRespondents = useCallback(() => {
    setSelectedRespondentItems([]);
    setSelectedRespondentKeysMap({});
    setRespondentTypesMap({});
  }, []);

  const onLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      setRespondentListOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (recipientList?.data?.items) {
      if (respondentListOffset === 0) {
        setRespondentList(recipientList.data.items);
      } else {
        setRespondentList((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = recipientList.data.items.filter((item) => !existingIds.has(item.id));
          return [...prev, ...newItems];
        });
      }
    }
  }, [recipientList, respondentListOffset]);

  useEffect(() => {
    if (!open) {
      return;
    }

    handleResetSelectedRespondents();
  }, [handleResetSelectedRespondents, open]);

  const handleSelectRespondentItem = (item: IRespondentRequest | null, respondentKey?: string) => {
    if (!item) {
      handleResetSelectedRespondents();
      return;
    }

    if (!respondentKey) {
      return;
    }

    const normalizedEmail = normalizeEmail(item.email);
    const exists = selectedRespondentKeysMap[normalizedEmail] === respondentKey;

    if (exists) {
      setSelectedRespondentKeysMap((prev) => {
        const next = { ...prev };
        delete next[normalizedEmail];
        return next;
      });

      setSelectedRespondentItems((prev) => (
        prev.filter((respondent) => normalizeEmail(respondent.email) !== normalizedEmail)
      ));
      return;
    }

    setSelectedRespondentKeysMap((prev) => ({
      ...prev,
      [normalizedEmail]: respondentKey,
    }));

    setSelectedRespondentItems((prev) => {
      const respondentType = item.respondentType || respondentTypesMap[respondentKey] || '';
      const respondentWithType: IRespondentRequest = {
        ...item,
        respondentType,
      };

      return [
        ...prev.filter((respondent) => normalizeEmail(respondent.email) !== normalizeEmail(item.email)),
        respondentWithType,
      ];
    });
  };

  const handleRespondentTypeChange = (
    item: IRespondentRequest,
    respondentKey: string,
    selectedOption: unknown,
  ) => {
    const option = selectedOption as IAnswerTypeOption | null;
    const newType = option?.value || '';
    const normalizedEmail = normalizeEmail(item.email);

    setRespondentTypesMap((prev) => ({
      ...prev,
      [respondentKey]: newType,
    }));

    setSelectedRespondentItems((prev) => prev.map((respondent) => {
      if (normalizeEmail(respondent.email) === normalizedEmail) {
        return {
          ...respondent,
          respondentType: newType,
        };
      }
      return respondent;
    }));
  };

  const handleUnSelectAll = () => {
    handleSelectRespondentItem(null);
  };

  const handleSelectItem = (item: IRespondentRequest, respondentKey: string) => {
    handleSelectRespondentItem(item, respondentKey);
  };

  const handleCancel = () => {
    handleResetSelectedRespondents();
    onClose();
  };

  const handleSend = () => {
    if (onShareSurvey && currentVcId && selectedItems) {
      onShareSurvey(
        selectedRespondentItems.map((item) => ({ email: item.email, respondentType: item.respondentType })),
        currentVcId,
        selectedItems,
      );
      handleCancel();
    }
  };

  const getRespondentKey = (item: IRespondentRequest, index: number) => [
    item.id ?? index,
    normalizeEmail(item.email),
    item.name || '',
  ].join('_');

  const isSendDisabled = selectedRespondentItems.length === 0
    || selectedRespondentItems.some((item) => !item.respondentType || item.respondentType.trim() === '');

  const menuPortalTarget = typeof document === 'undefined' ? undefined : document.body;

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
      isLoading={isLoading}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.wrapperContent}>
          <div className={styles.tableWrapper}>
            <div className={styles.rootTable}>
              <div className={styles.actionsWrapper}>
                <div className={styles.searchWrapper}>
                  <TextField
                    name="respondentListKeyword"
                    value={respondentListKeyword}
                    placeholder={t('common.searchByName')}
                    icon={<SearchIcon />}
                    onChange={(event) => handleChangeRespondentListKeyword(event.target.value)}
                  />
                </div>
                <div className={styles.buttonsWrapper}>
                  <Button
                    title={t('materialityAssessment.addEmailButton')}
                    startIcon={<PlusIcon />}
                    variant="ghost"
                    onClick={handleAddEmail}
                    className={styles.downloadButton}
                  />
                </div>
              </div>
              {items && items.length === 0 && !isLoadingMore && !respondentListKeyword ? (
                <EmptyTaskManagement
                  title={t('materialityAssessment.emptyRespondentTable')}
                  buttonTitle={t('materialityAssessment.addEmailButton')}
                  buttonOnClick={handleAddEmail}
                  checkIcon
                />
              ) : null}

              {items && items.length === 0 && !isLoadingMore && respondentListKeyword ? (
                <EmptyList text={emptyListText} />
              ) : null}
              {items && items.length > 0 ? (
                <div
                  className={styles.tableBodyScroll}
                  id="scrollableDiv"
                  aria-label="Analysis table body"
                  role="region"
                >
                  <InfiniteScroll
                    dataLength={items.length}
                    next={onLoadMore}
                    hasMore={hasMore}
                    style={{ overflow: 'visible' }}
                    loader={(
                      <div className={styles.loader}>
                        {t('common.loading')}
                      </div>
                    )}
                    scrollableTarget="scrollableDiv"
                  >
                    <Table
                      className={clsx(styles.table, {
                        [styles.tableInfoTable]: isInfoTable,
                      })}
                    >
                      <TableHead className={clsx(styles.header, {
                        [styles.headerInfoTable]: isInfoTable,
                      })}
                      >
                        {HEAD_IRO_SEND_LOCAL_EMAILS_CELLS.map(({
                          id, label,
                        }, index) => (
                          <TableCell head className={styles.tableCell} key={id}>
                            <div>
                              <span className={styles.tableCellLabel}>
                                {showHeaderCheckbox && index === 0 ? (
                                  <div className={styles.checkboxLabel}>
                                    <div className={styles.checkboxColumn}>
                                      <Checkbox
                                        name="vs-all"
                                        className={clsx(
                                          styles.checkbox,
                                          [selectedRespondentItems.length === 0 && styles.disabled],
                                        )}
                                        checked={selectedRespondentItems.length > 0}
                                        value={String(selectedRespondentItems.length > 0)}
                                        onChange={handleUnSelectAll}
                                        label={false}
                                        inputAriaLabel={t('common.select')}
                                        disabled={selectedRespondentItems.length === 0}
                                        isLineMark
                                      />
                                    </div>
                                    {t(label)}
                                  </div>
                                ) : t(label)}
                              </span>
                            </div>
                          </TableCell>
                        ))}
                      </TableHead>
                      <TableBody className={styles.body}>
                        {items ? items?.map((item, index) => {
                          const respondentKey = getRespondentKey(item, index);
                          const normalizedEmail = normalizeEmail(item.email);
                          const isSelected = selectedRespondentKeysMap[normalizedEmail] === respondentKey;

                          return (
                            <TableRow key={respondentKey}>
                              <TableCell>
                                <div className={styles.staticCell}>
                                  <div className={styles.checkboxColumn}>
                                    <Checkbox
                                      key={respondentKey}
                                      name={`${SELECTED_ITEMS}-${respondentKey}`}
                                      checked={isSelected}
                                      value={item.email}
                                      onChange={() => handleSelectItem(item, respondentKey)}
                                      label={false}
                                      inputAriaLabel={t('common.select')}
                                      isLineMark={false}
                                    />
                                  </div>
                                  <span>
                                    {item.name || hyphen}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className={styles.emailCell}>
                                  {item.email || hyphen}
                                </div>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const selectedRespondent = isSelected
                                    ? selectedRespondentItems.find(
                                      (respondent) => normalizeEmail(respondent.email) === normalizedEmail,
                                    )
                                    : undefined;
                                  const typeFromServer = item.respondentType;
                                  const typeFromSelector = selectedRespondent?.respondentType
                                    || respondentTypesMap[respondentKey];

                                  if (typeFromServer) {
                                    return t(`materialityAssessment.${typeFromServer}`, typeFromServer);
                                  }

                                  return (
                                    <Select
                                      name={`respondentType-${respondentKey}`}
                                      value={respondentOptions.find(
                                        (option) => option.value === typeFromSelector,
                                      )}
                                      options={respondentOptions}
                                      placeholder={t('materialityAssessment.respondentRole')}
                                      onChange={(selectedOption: unknown) => {
                                        handleRespondentTypeChange(item, respondentKey, selectedOption);
                                      }}
                                      formatOptionLabel={(option: IAnswerTypeOption) => (
                                        <div>
                                          <span>
                                            {t(`materialityAssessment.${option.label}`)}
                                          </span>
                                        </div>
                                      )}
                                      menuPosition="fixed"
                                      menuPortalTarget={menuPortalTarget}
                                      maxMenuHeight={RESPONDENT_TYPE_MENU_MAX_HEIGHT}
                                      modalRef={modalRef}
                                      className={styles.respondentTypeSelect}
                                    />
                                  );
                                })()}
                              </TableCell>
                            </TableRow>
                          );
                        }) : null}
                      </TableBody>
                    </Table>
                  </InfiniteScroll>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={handleCancel}
          />
          <Button
            title={titleSend}
            onClick={handleSend}
            className={styles.buttonSend}
            disabled={isSendDisabled}
          />
        </div>
      </div>
    </Modal>
  );
}

export default RespondentsModal;
